/**
 * FamiliarisationCustomizerView - Full-screen checklist customization
 * Allows complete flexibility: edit/delete standard items, create custom checklists, paper mode
 *
 * FT-115: REWRITTEN to use useFamiliarisationTemplates as single source of truth.
 * Previously wrote to 4 different storage keys (2 unscoped = broken sync).
 * Now all edits go through the hook → single storage key → proper sync.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Edit2, Trash2, FileText, Settings, Save } from 'lucide-react';
import { FAMILIARISATION_SECTIONS } from '../../../../config/familiarisationItems5Sections';
import { generateFamiliarisationWithCustomItems } from '../../../../utils/familiarisationHelpers';
import { useVessel } from 'contexts/VesselContext';
import { useFamiliarisationTemplates } from '../../../../hooks/useFamiliarisationTemplates';
import FamiliarisationPaperMode from './FamiliarisationPaperMode';
import TemplateCard from './TemplateCard';
import CreateTemplateModal from './CreateTemplateModal';
import EditTemplateView from './EditTemplateView';

const FamiliarisationCustomizerView = ({ onBack }) => {
  const { getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  const [activeTab, setActiveTab] = useState('templates');  // Default to templates
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ text: '', section: 'pre-sailing', priority: 'normal' });
  const [hasChanges, setHasChanges] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // FT-115: SINGLE SOURCE OF TRUTH - all data from useFamiliarisationTemplates
  const {
    templates,
    addTemplate,
    updateTemplate: updateTemplateData,
    deleteTemplate,
    duplicateTemplate,
    setDefaultTemplate,
    // FT-115: Settings (consolidated)
    settings,
    updateSettings,
    // FT-115: Customizations (consolidated)
    customizations,
    addCustomItem,
    updateCustomItem,
    deleteCustomItem,
    editStandardItem,
    deleteStandardItem,
    restoreStandardItem,
    resetCustomizations
  } = useFamiliarisationTemplates();

  // Local state derived from hook data (for UI only)
  const editedItems = customizations.editedItems;
  const deletedItems = customizations.deletedItems;
  const customItems = customizations.customItems;
  const paperMode = settings.paperMode;

  // Template editing state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // CRW-005: Validity period UI state
  const [validityPeriod, setValidityPeriod] = useState(12);
  const [customPeriod, setCustomPeriod] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // FT-115: Load validity period from settings (single source of truth)
  useEffect(() => {
    const savedPeriod = settings.validityPeriodMonths;
    if (savedPeriod === 0) {
      setValidityPeriod(0); // No expiry
      setShowCustomInput(false);
    } else if (savedPeriod && ![6, 12, 18, 24, 36].includes(savedPeriod)) {
      setValidityPeriod('custom');
      setCustomPeriod(savedPeriod.toString());
      setShowCustomInput(true);
    } else {
      setValidityPeriod(savedPeriod || 12);
      setShowCustomInput(false);
    }
  }, [settings.validityPeriodMonths]);

  // Get all items from the system
  const getAllItems = () => {
    if (!currentVessel) return [];
    
    // Generate a dummy crew member to get all possible items
    const dummyCrew = { position: 'master' };
    const familiarisation = generateFamiliarisationWithCustomItems(currentVessel, dummyCrew, []);
    
    const allItems = [];
    familiarisation.sections.forEach(section => {
      section.items.forEach(item => {
        allItems.push({
          ...item,
          section: section.section,
          sectionTitle: section.title
        });
      });
    });
    
    return allItems;
  };

  // FT-115: Save goes through hook (single source of truth, automatic sync)
  const handleSave = useCallback(() => {
    // Save validity period
    const periodToSave = validityPeriod === 'custom'
      ? parseInt(customPeriod) || 12
      : validityPeriod;

    updateSettings({
      validityPeriodMonths: periodToSave,
      paperMode: paperMode
    });

    // Note: customizations are already saved on each edit via the hook
    // This save button just saves the validity period setting

    setHasChanges(false);
    alert('Settings saved successfully!');
  }, [validityPeriod, customPeriod, paperMode, updateSettings]);

  // FT-115: Reset goes through hook
  const handleReset = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all customizations?')) {
      resetCustomizations();
      setHasChanges(false);
    }
  }, [resetCustomizations]);

  // FT-115: All item operations go through hook
  const handleAddCustomItem = useCallback(() => {
    if (!newItem.text.trim()) {
      alert('Please enter item text');
      return;
    }

    addCustomItem({
      ...newItem,
      isCustom: true
    });

    setNewItem({ text: '', section: 'pre-sailing', priority: 'normal' });
    setShowAddForm(false);
    setHasChanges(true);
  }, [newItem, addCustomItem]);

  const handleEditItem = (item) => {
    setEditingItem(item);
  };

  const handleSaveEdit = useCallback(() => {
    if (editingItem.isCustom) {
      updateCustomItem(editingItem.id, {
        text: editingItem.text || editingItem.topic,
        topic: editingItem.text || editingItem.topic
      });
    } else {
      editStandardItem(editingItem.id, editingItem.text || editingItem.topic);
    }
    setEditingItem(null);
    setHasChanges(true);
  }, [editingItem, updateCustomItem, editStandardItem]);

  const handleDeleteItem = useCallback((item) => {
    if (item.isCustom) {
      deleteCustomItem(item.id);
    } else {
      deleteStandardItem(item.id);
    }
    setHasChanges(true);
  }, [deleteCustomItem, deleteStandardItem]);

  const handleRestoreItem = useCallback((itemId) => {
    restoreStandardItem(itemId);
    setHasChanges(true);
  }, [restoreStandardItem]);

  // Template actions
  const handleCreateTemplate = (newTemplate) => {
    const result = addTemplate(newTemplate);
    if (result.success) {
      // Optionally open editor for the new template
      setEditingTemplate(result.template);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
  };

  const handleSaveTemplate = (updatedTemplate) => {
    updateTemplateData(updatedTemplate.id, updatedTemplate);
    setEditingTemplate(null);
  };

  const handleDuplicateTemplate = (template) => {
    const newName = prompt('Enter name for the duplicate:', `${template.name} (Copy)`);
    if (newName) {
      duplicateTemplate(template.id, newName);
    }
  };

  const handleDeleteTemplate = (template) => {
    if (window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
      deleteTemplate(template.id);
    }
  };

  const handleSetDefault = (template) => {
    setDefaultTemplate(template.id);
  };

  const allItems = getAllItems();
  const sections = [...new Set(allItems.map(item => item.section))];

  // If editing a template, show the editor
  if (editingTemplate) {
    return (
      <EditTemplateView
        template={editingTemplate}
        onSave={handleSaveTemplate}
        onBack={() => setEditingTemplate(null)}
        onDelete={(id) => {
          deleteTemplate(id);
          setEditingTemplate(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Customize Familiarisation Checklist
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Add, edit, or remove items from the familiarisation checklist
                </p>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
                  Unsaved changes
                </span>
              )}
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Reset All
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'templates'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="inline-block h-4 w-4 mr-2" />
              Templates
            </button>
            <button
              onClick={() => setActiveTab('customize')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'customize'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Edit2 className="inline-block h-4 w-4 mr-2" />
              Customize Items
            </button>
            <button
              onClick={() => setActiveTab('paper')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'paper'
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="inline-block h-4 w-4 mr-2" />
              Paper Mode
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Familiarisation Templates</h3>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm hover:bg-cyan-700 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  New Template
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Create different checklists for crew, contractors, and custom use cases.
                Each template can have its own validity period and apply to specific positions.
              </p>

              {templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No templates yet. Create your first template to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(template => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onEdit={handleEditTemplate}
                      onDuplicate={handleDuplicateTemplate}
                      onDelete={handleDeleteTemplate}
                      onSetDefault={handleSetDefault}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Create Template Modal */}
            <CreateTemplateModal
              isOpen={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onCreate={handleCreateTemplate}
              existingTemplates={templates}
            />
          </div>
        )}

        {activeTab === 'customize' && (
          <div className="space-y-6">
            {/* CRW-005: Validity Period Setting */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Familiarisation Validity</h3>
              <div className="flex items-center gap-4">
                <label className="text-sm text-slate-600">
                  Familiarisation records are valid for:
                </label>
                <select
                  value={validityPeriod}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'custom') {
                      setValidityPeriod('custom');
                      setShowCustomInput(true);
                    } else {
                      setValidityPeriod(parseInt(val));
                      setShowCustomInput(false);
                    }
                    setHasChanges(true);
                  }}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value={0}>No expiry</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months (default)</option>
                  <option value={18}>18 months</option>
                  <option value={24}>24 months</option>
                  <option value={36}>36 months</option>
                  <option value="custom">Custom...</option>
                </select>
                {showCustomInput && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={customPeriod}
                      onChange={(e) => {
                        setCustomPeriod(e.target.value);
                        setHasChanges(true);
                      }}
                      placeholder="Months"
                      className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                    <span className="text-sm text-slate-600">months</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {validityPeriod === 0
                  ? 'Familiarisation will never expire once completed.'
                  : 'Crew will show as "Familiarisation Overdue" after this period.'}
              </p>
            </div>

            {/* Add Custom Item */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add Custom Item</h3>
              
              {!showAddForm ? (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-cyan-600 bg-cyan-50 rounded-md hover:bg-cyan-100"
                >
                  <Plus className="h-4 w-4" />
                  Add New Item
                </button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item Text
                    </label>
                    <input
                      type="text"
                      value={newItem.text}
                      onChange={(e) => setNewItem({ ...newItem, text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      placeholder="Enter checklist item text..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Section
                      </label>
                      <select
                        value={newItem.section}
                        onChange={(e) => setNewItem({ ...newItem, section: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      >
                        {sections.map(section => (
                          <option key={section} value={section}>{section}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <select
                        value={newItem.priority}
                        onChange={(e) => setNewItem({ ...newItem, priority: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      >
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddCustomItem}
                      className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                    >
                      Add Item
                    </button>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setNewItem({ text: '', section: 'pre-sailing', priority: 'normal' });
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Existing Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Checklist Items</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Edit or delete existing items, or restore deleted items
                </p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {sections.map(section => {
                  const sectionItems = [
                    ...allItems.filter(item => 
                      item.section === section && 
                      !deletedItems.includes(item.id)
                    ).map(item => ({
                      ...item,
                      ...(editedItems[item.id] || {})
                    })),
                    ...customItems.filter(item => item.section === section)
                  ];
                  
                  const deletedSectionItems = allItems.filter(item => 
                    item.section === section && 
                    deletedItems.includes(item.id)
                  );
                  
                  return (
                    <div key={section} className="px-6 py-4">
                      <h4 className="font-medium text-gray-900 mb-3 capitalize">
                        {section.replace('-', ' ')}
                      </h4>
                      
                      <div className="space-y-2">
                        {sectionItems.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            {editingItem?.id === item.id ? (
                              <div className="flex-1 mr-4">
                                <input
                                  type="text"
                                  value={editingItem.text || editingItem.topic}
                                  onChange={(e) => setEditingItem({ 
                                    ...editingItem, 
                                    text: e.target.value,
                                    topic: e.target.value 
                                  })}
                                  className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                />
                              </div>
                            ) : (
                              <div className="flex-1">
                                <span className="text-sm text-gray-900">
                                  {item.text || item.topic}
                                </span>
                                {item.isCustom && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                                    Custom
                                  </span>
                                )}
                                {editedItems[item.id] && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                                    Edited
                                  </span>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2">
                              {editingItem?.id === item.id ? (
                                <>
                                  <button
                                    onClick={handleSaveEdit}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingItem(null)}
                                    className="text-gray-600 hover:text-gray-700"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEditItem(item)}
                                    className="text-cyan-600 hover:text-cyan-700"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteItem(item)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {deletedSectionItems.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-sm text-gray-500 mb-2">Deleted items:</p>
                            {deletedSectionItems.map(item => (
                              <div key={item.id} className="flex items-center justify-between p-2 text-sm text-gray-400">
                                <span className="line-through">{item.topic}</span>
                                <button
                                  onClick={() => handleRestoreItem(item.id)}
                                  className="text-cyan-600 hover:text-cyan-700"
                                >
                                  Restore
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'paper' && (
          <FamiliarisationPaperMode
            paperMode={paperMode}
            setPaperMode={(value) => {
              updateSettings({ paperMode: value });
              setHasChanges(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default FamiliarisationCustomizerView;